import React, { useEffect, useState } from 'react';

import { Button } from '@components/Button';
import { FormItem, FormItemProps } from '@components/Forms/FormItem';
import { makeFormItemProps } from '@components/Forms/helpers';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { type ClassMap } from '@styles';
import { findFirstPath } from '@utils/types';
import { useField, useFormikContext } from 'formik';
import omit from 'lodash-es/omit';

export interface BaseInputProps {
  name: string;
  label?: React.ReactNode;
  noLabel?: boolean;
  helperText?: string;
  formClasses?: FormClasses;
  hintText?: string;
  hintTitle?: string;
  itemVariant?: FormItemProps['variant'];
  itemErrorVariant?: FormItemProps['errorVariant'];
}

export const extractOwnProps = <T extends BaseInputProps>(props: T) => {
  return omit(props, [
    'name',
    'label',
    'formClasses',
    'helperText',
    'noLabel',
    'hintText',
    'hintTitle',
    'itemVariant',
    'itemErrorVariant',
  ]);
};

type FormClasses = ClassMap<'label' | 'root' | 'itemContentContainer'>;

export interface PersistFormProps {
  name: string;
  disabled?: boolean;
  rehydrate?: (form: any) => any;
  persist?: (stored: any) => any;
  initialValues?: any;
}

export const PersistForm: React.FC<PersistFormProps> = (props) => {
  const { name, disabled = false, rehydrate = (x) => x, persist = (x) => x, initialValues } = props;

  const { values, setValues, setTouched, setErrors, validateForm } = useFormikContext();

  const [curName, setCurName] = useState<string>();

  useEffect(() => {
    if (disabled) return;
    const value = localStorage.getItem(name);
    if (value) {
      try {
        const rehydrated = rehydrate(JSON.parse(value));
        const touched = Object.fromEntries(Object.keys(rehydrated).map((key) => [key, true]));

        setValues(rehydrated);
        setTouched(touched);

        if (validateForm) {
          validateForm(rehydrated).then(setErrors);
        }
      } catch (e) {
        console.warn(`Tried to load form ${name} from localStorage, but got error`, e);
      }
    } else if (initialValues !== undefined) {
      setValues(initialValues);
    }

    setCurName(name);
  }, [disabled]);

  useEffect(() => {
    if (disabled) return;
    if (curName === undefined || curName === name) {
      localStorage.setItem(name, JSON.stringify(persist(values)));
      setCurName(curName);
    }
  }, [disabled, JSON.stringify(values as any)]);

  return null;
};

export const LogForm: React.FC<PersistFormProps> = (props) => {
  const { name, disabled = import.meta.env.PROD } = props;

  const { values, errors } = useFormikContext();

  useEffect(() => {
    if (disabled) return;

    // eslint-disable-next-line no-console
    console.debug(name, values);
    if (Object.keys(errors).length > 0) {
      // eslint-disable-next-line no-console
      console.debug(`${name}:error`, errors);
    }
  }, [values, errors, disabled]);

  return null;
};

export interface RadioGroupInputProps extends BaseInputProps {
  label: string;
  options: {
    value: string | number | boolean;
    label: React.ReactNode;
    disabled?: boolean;
  }[];
  formControlLabelClasses?: React.ComponentProps<typeof FormControlLabel>['classes'];
  radioGroupClasses?: React.ComponentProps<typeof RadioGroup>['classes'];
  iconSize?: 'small' | 'medium';
  disabled?: boolean;
}

export const RadioGroupInput: React.FC<RadioGroupInputProps> = (props) => {
  const {
    name,
    options,
    formControlLabelClasses = {},
    radioGroupClasses = {},
    iconSize = 'medium',
    disabled,
  } = props;

  const [field, , helpers] = useField<any>(name);

  const { isSubmitting = false } = useFormikContext() ?? {};

  return (
    <FormItem {...makeFormItemProps(props)}>
      <RadioGroup classes={radioGroupClasses}>
        {options.map((option) => {
          const finalDisabled = disabled || isSubmitting || option.disabled;
          return (
            <FormControlLabel
              key={String(option.value)}
              value={option.value}
              control={<Radio color="primary" size={iconSize} disabled={isSubmitting} />}
              label={option.label}
              disabled={finalDisabled}
              classes={formControlLabelClasses}
              onClick={() => !finalDisabled && helpers.setValue(option.value)}
              checked={field.value === option.value}
            />
          );
        })}
      </RadioGroup>
    </FormItem>
  );
};

export const ButtonInput: typeof Button = React.forwardRef((props, ref) => {
  const { onClick: customOnClick, ...rest } = props;
  const { errors } = useFormikContext();

  const onClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    customOnClick?.(e);
    if (errors) {
      const key = findFirstPath(errors);

      const scrollTarget = document.querySelector(`*[name='${key}']`);
      scrollTarget?.scrollIntoView({ block: 'center' });
    }
  };

  return <Button ref={ref} type="submit" onClick={onClick} {...rest} />;
});
